import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  Gift,
  Loader2,
  Lock,
  ShieldCheck,
  X,
} from "lucide-react";
import PlanCard from "./PlanCard";
import CurrencyToggle from "./CurrencyToggle";
import Modal from "../Modal";
import type { PaywallRequest } from "../../context/paywall-context";
import {
  getPlans,
  setPreferredCurrency,
  startCheckout,
  type BillingCurrency,
  type CatalogOffer,
  type Plan,
  type PlanTier,
} from "../../services/billingApi";
import { useEntitlements } from "../../hooks/useEntitlements";
import { useCheckoutDesign } from "../../hooks/useCheckoutDesign";

type PaywallModalProps = {
  request: PaywallRequest | null;
  onClose: () => void;
};

const DEFAULT_TIERS: PlanTier[] = ["BASIC", "PREMIUM", "PLATINUM", "ULTRA_PRO"];

export default function PaywallModal({ request, onClose }: PaywallModalProps) {
  const { entitlements } = useEntitlements();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [currency, setCurrency] = useState<BillingCurrency | null>(null);
  const [activeOffer, setActiveOffer] = useState<CatalogOffer | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyTier, setBusyTier] = useState<PlanTier | null>(null);

  const isOpen = request !== null;
  const { templateKey, basicDisabledReason, isPaid } = useCheckoutDesign(
    request?.templateKey,
    isOpen,
  );

  const loadPlans = useCallback(async (override?: BillingCurrency) => {
    setIsLoading(true);
    setError(null);
    try {
      const catalog = await getPlans(override);
      setPlans(catalog.plans);
      setCurrency(catalog.currency);
      setActiveOffer(catalog.activeOffer ?? null);
    } catch {
      setError("Could not load pricing. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // The catalog is fetched per open so currency routing reflects the visitor.
  useEffect(() => {
    if (!isOpen) return;
    void loadPlans();
  }, [isOpen, loadPlans]);

  const handleSelect = async (tier: PlanTier) => {
    if (busyTier || (tier === "BASIC" && basicDisabledReason)) return;
    setBusyTier(tier);
    setError(null);
    try {
      // Navigates away to PayHere; entitlements land via the webhook.
      await startCheckout({
        planTier: tier,
        templateKey,
        currency: currency ?? undefined,
        returnPath:
          request?.returnPath ??
          `${window.location.pathname}${window.location.search}`,
      });
    } catch (err) {
      setBusyTier(null);
      setError(
        err instanceof Error
          ? err.message
          : "Could not start checkout. Please try again.",
      );
    }
  };

  if (!isOpen || !request) return null;

  const offered = request.suggestedTiers ?? DEFAULT_TIERS;
  const visiblePlans = plans.filter(
    (plan) =>
      offered.includes(plan.tier) ||
      (plan.tier === "BASIC" && isPaid && !request.feature),
  );
  // Promote the cheapest plan that actually resolves the block.
  const featuredTier = offered[0];
  const isBasicDesignNotice = request.notice === "basic-design";

  return (
    <Modal
      open
      onClose={onClose}
      showCloseButton={false}
      scrollBody={false}
      bodyClassName=""
      // Capped + flex-col so the header (with the close button) stays put
      // and only the plans/footer scroll — on a short viewport the panel no
      // longer grows past the screen with no way to reach what's clipped.
      panelClassName="flex max-h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl"
    >
      <div
        className={`relative shrink-0 border-b p-5 sm:p-6 ${
          isBasicDesignNotice
            ? "border-t-4 border-amber-200 border-t-amber-500 bg-gradient-to-r from-amber-50 to-orange-50"
            : "border-gray-100"
        }`}
        data-tour="paywall-modal"
      >
        <div className="flex max-h-[55vh] min-w-0 items-start gap-4 overflow-y-auto overscroll-contain">
          <div
            className={`hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl sm:flex ${
              isBasicDesignNotice
                ? "bg-amber-500 text-amber-950 shadow-sm"
                : "bg-indigo-100 text-indigo-600"
            }`}
          >
            {isBasicDesignNotice ? (
              <AlertCircle className="h-7 w-7" />
            ) : (
              <Lock className="h-6 w-6" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            {isBasicDesignNotice && (
              <div className="mb-2 pr-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-200 px-3 py-1 text-xs font-bold uppercase tracking-wide text-amber-950">
                  <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
                  Design not included
                </span>
              </div>
            )}
            <h2
              className={`pr-10 font-extrabold tracking-tight ${
                isBasicDesignNotice
                  ? "text-xl leading-tight text-amber-950 sm:text-2xl"
                  : "text-xl text-gray-900"
              }`}
            >
              {request.title}
            </h2>
            <p
              className={`mt-2 max-w-3xl text-sm font-medium leading-relaxed ${
                isBasicDesignNotice ? "text-amber-900" : "text-gray-500"
              }`}
            >
              {request.description}
            </p>
            {isBasicDesignNotice && (
              <p className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm font-bold text-emerald-800">
                <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden="true" />
                This attempt did not deduct any download credits.
              </p>
            )}
            {request.onUseLegacy && (isPaid || isBasicDesignNotice) && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => {
                    request.onUseLegacy?.();
                    onClose();
                  }}
                  className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-indigo-700 px-5 py-3 text-sm font-bold text-white shadow-md shadow-indigo-900/15 transition-colors hover:bg-indigo-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-700 sm:w-auto"
                >
                  Use Classic Legacy
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className={`absolute right-3 top-4 rounded-lg p-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 sm:right-4 ${
            isBasicDesignNotice
              ? "text-amber-800 hover:bg-amber-200 hover:text-amber-950 focus-visible:outline-amber-800"
              : "text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline-indigo-600"
          }`}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {/* Live promotion — the discounted figures on the cards below are
              what checkout charges, so say why they're struck through. */}
        {activeOffer && (
          <div className="mb-5 flex items-center gap-3 rounded-2xl bg-gradient-to-r from-rose-500 to-indigo-600 px-4 py-3 text-white shadow-md shadow-rose-500/20">
            <Gift className="h-5 w-5 shrink-0" />
            <p className="text-sm font-semibold">
              {activeOffer.discountPercent}% off — {activeOffer.headline}.{" "}
              <span className="font-medium text-white/85">
                {activeOffer.autoApply
                  ? "Applied automatically at checkout."
                  : `Use code ${activeOffer.code} on the pricing page.`}
              </span>
            </p>
          </div>
        )}

        {currency && (
          <div className="mb-5 flex items-center justify-between gap-4">
            <p className="text-xs font-semibold text-gray-400">
              Basic Pass is a one-time payment · Premium and above renew
              monthly, cancel anytime
            </p>
            <CurrencyToggle
              currency={currency}
              onChange={(next) => {
                setPreferredCurrency(next);
                setCurrency(next);
                void loadPlans(next);
              }}
            />
          </div>
        )}

        {error && (
          <div className="mb-5 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span className="text-sm font-semibold">{error}</span>
          </div>
        )}

        {isLoading && plans.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-indigo-600" />
          </div>
        ) : (
          <div
            className={`grid gap-5 ${
              visiblePlans.length >= 3
                ? "sm:grid-cols-2 lg:grid-cols-3"
                : "sm:grid-cols-2"
            }`}
          >
            {visiblePlans.map((plan) => (
              <PlanCard
                key={plan.tier}
                plan={plan}
                currency={currency ?? "LKR"}
                action={entitlements?.availableActions.find(
                  (entry) => entry.tier === plan.tier,
                )}
                isFeatured={plan.tier === featuredTier}
                isBusy={busyTier === plan.tier}
                disabled={busyTier !== null}
                disabledReason={
                  plan.tier === "BASIC" ? basicDisabledReason : undefined
                }
                onSelect={(tier) => void handleSelect(tier)}
              />
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-5">
          <p className="text-xs font-medium text-gray-400">
            Secured by PayHere · Cards, eZ Cash, mCash & internet banking
          </p>
          <Link
            to={
              templateKey
                ? `/pricing?template=${encodeURIComponent(templateKey)}`
                : "/pricing"
            }
            onClick={onClose}
            className="text-xs font-bold text-indigo-600 hover:text-indigo-700 hover:underline"
          >
            Compare all plans →
          </Link>
        </div>
      </div>
    </Modal>
  );
}
